import Image from "next/image";
import { getProductImage } from "@/lib/product-images";
import { getEmoji } from "@/lib/emoji";
import { resolveImageUrl } from "@/lib/utils";

interface ProductImageProps {
  slug: string;
  name: string;
  categorySlug?: string;
  /** Admin panelden yüklenen gerçek foto (DB, hf_products.image_url). Varsa manifest.json'dan önce kullanılır. */
  imageUrl?: string | null;
  /** Varyantta fotoğraf yoksa güvenli biçimde master ürünün fotoğrafını kullanır. */
  canonicalSlug?: string | null;
  /** Görüntü boyutu (px). Default 80. */
  size?: number;
  className?: string;
  /** Sadece sayfa açılışında görünen TEK bir görsel için true yapın (LCP). Liste/ticker/kart gibi tekrarlı kullanımlarda false kalmalı. */
  priority?: boolean;
  /**
   * Gorsel CSS ile `size`'dan farkli genislikte cizildiginde (ornegin !w-full kapak)
   * gercek goruntu genisligi. Verilmezse `${size}px`: 720px kapak mobilde 372px
   * cizilirken 1920w varyanti indiriliyordu (PageSpeed, 28 Eyl 2026).
   */
  sizes?: string;
}

function compactCatalogSource(src: string): string | null {
  const prefix = "/images/urunler/catalog-v1/";
  if (src.startsWith(prefix)) return src.replace(prefix, "/images/urunler/thumb-v1/");
  const site = process.env.NEXT_PUBLIC_SITE_URL;
  if (!site || !/^https?:\/\//.test(src)) return null;
  try {
    const image = new URL(src);
    if (image.origin !== new URL(site).origin || image.search || image.hash || !image.pathname.startsWith(prefix)) return null;
    return image.pathname.replace(prefix, "/images/urunler/thumb-v1/");
  } catch {
    return null;
  }
}

/**
 * Ürün fotoğraf bileşeni — detay sayfası, kart, liste, ticker, arama sonucu
 * fark etmeksizin her yerde kullanılabilir. Fotoğraf varsa gösterir, yoksa
 * emoji fallback döner.
 */
export default function ProductImage({
  slug,
  name,
  categorySlug,
  imageUrl,
  canonicalSlug,
  size = 80,
  className = "",
  priority = false,
  sizes,
}: ProductImageProps) {
  const src = (imageUrl ? resolveImageUrl(imageUrl, "") : "") || getProductImage(slug, canonicalSlug);
  const isCatalogCutout = src?.includes("/images/urunler/catalog-v1/") ?? false;
  const isCompact = size <= 96;
  // Liste görselleri derleme sırasında 192 px hazırlanır; canlı Next görsel dönüştürücüsünü kullanmaz.
  const thumbnail = isCompact && !sizes && src ? compactCatalogSource(src) : null;
  const isSeamless = isCatalogCutout || isCompact;

  if (src) {
    return (
      <div
        className={`shrink-0 overflow-hidden ${
          isSeamless
            ? "bg-transparent"
            : "rounded-2xl border border-(--color-border) bg-(--color-surface) shadow-sm"
        } ${className}`}
        style={{ width: size, height: size }}
      >
        <Image
          src={thumbnail ?? src}
          alt={`${name} ürün görseli`}
          width={size}
          height={size}
          sizes={sizes}
          className={
            isCatalogCutout
              ? "h-full w-full object-contain"
              : isCompact
                ? "h-full w-full object-cover [mask-image:radial-gradient(circle_at_center,#000_62%,transparent_100%)]"
                : "h-full w-full object-cover"
          }
          priority={priority}
          unoptimized={Boolean(thumbnail)}
        />
      </div>
    );
  }

  const emoji = getEmoji(slug, categorySlug);
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-(--color-border) bg-(--color-bg-alt) ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.55 }}
      role="img"
      aria-label={`${name} için ürün fotoğrafı bulunmuyor`}
    >
      {emoji}
    </span>
  );
}
